import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, FileDown, RefreshCw } from "lucide-react";
import LocationReportView from "@/components/LocationReportView";
import ShareDialog from "@/components/ShareDialog";

export default function SiteDetail() {
  const { id } = useParams();
  const { t } = useI18n();
  const [site, setSite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/sites/${id}`);
        setSite(data);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        nav("/app/sites");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, nav]);

  const refresh = async () => {
    if (!site) return;
    setRefreshing(true);
    try {
      const { data: report } = await api.post("/location/report", {
        latitude: site.latitude, longitude: site.longitude,
      });
      const { data: updated } = await api.patch(`/sites/${id}`, { location_report: report });
      setSite(updated);
      toast.success("Aktualisiert");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setRefreshing(false);
    }
  };

  const downloadPdf = async () => {
    if (!site?.location_report) return;
    setDownloading(true);
    try {
      const resp = await api.post(
        "/location/pdf",
        { report: site.location_report, site_name: site.name },
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${site.name.replace(/\s+/g, "_").toLowerCase()}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setDownloading(false);
    }
  };

  if (loading) return <div className="text-[#5C6B61]">{t("common.loading")}</div>;
  if (!site) return null;

  return (
    <div className="max-w-6xl mx-auto" data-testid="site-detail-page">
      <Link to="/app/sites" className="inline-flex items-center text-sm text-[#5C6B61] hover:text-[#1A2F24] mb-4" data-testid="back-to-sites">
        <ArrowLeft size={14} className="mr-1" /> {t("common.back")}
      </Link>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tight text-[#1A2F24] mb-1">
            {site.name}
          </h1>
          <p className="text-[#5C6B61] text-sm">{site.address}</p>
          {site.description && <p className="text-[#5C6B61] text-sm mt-2 max-w-2xl">{site.description}</p>}
        </div>
        <div className="flex gap-2 flex-wrap">
          <ShareDialog site={site} onChange={setSite} />
          <Button data-testid="refresh-report-button" onClick={refresh} disabled={refreshing} variant="outline" className="border-[#1A2F24] text-[#1A2F24] hover:bg-[#F1EFE7]">
            <RefreshCw size={16} className={`mr-2 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? t("common.loading") : "Neu laden"}
          </Button>
          <Button data-testid="site-download-pdf" onClick={downloadPdf} disabled={downloading || !site.location_report} className="bg-[#1A2F24] hover:bg-[#2E3B32] text-white">
            <FileDown size={16} className="mr-2" />
            {downloading ? t("common.loading") : "PDF"}
          </Button>
        </div>
      </div>

      {site.location_report ? (
        <LocationReportView report={site.location_report} />
      ) : (
        <div className="bg-white border border-[#E5E2D9] rounded-xl p-8 text-center text-[#5C6B61]">
          Noch keine Reportdaten. Klicken Sie auf &quot;Neu laden&quot; um die Standortdaten zu laden.
        </div>
      )}
    </div>
  );
}
